import { useDesignSystem } from '@/composables/designSystem'
import { useRoute } from '@/composables/route'
import { router, usePage } from '@inertiajs/vue3'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

/**
 * The choice between the two Suite looks, for the user menu (UserDropdown, in both looks).
 *
 * @returns {{
 *   available: import('vue').ComputedRef<boolean>,
 *   options: import('vue').ComputedRef<Array<{key: string, label: string, designSystem: boolean, active: boolean}>>,
 *   setAppearance: (useDesignSystem: boolean) => void,
 * }}
 */
export const useAppearance = () => {
  const { t } = useI18n()
  const designSystem = useDesignSystem()

  /**
   * Whether this user has a choice of look at all: their company has the design system, and they
   * remember the look it replaced. Not whether it is the look in force - those two differ exactly
   * when someone has opted out, which is when the switch most needs to be there. An account that
   * started on the design system never sees this control (_designSystemCanChoose).
   */
  const available = computed(
    () => usePage().props._designSystemCanChoose === true,
  )

  /**
   * Marked against the look actually in force, not the stored choice: someone who has never been
   * asked still has one of the two in front of them, and the menu should say which.
   */
  const options = computed(() => [
    {
      key: 'design-system',
      label: t('appearance.design_system'),
      designSystem: true,
      active: designSystem.value,
    },
    {
      key: 'classic',
      label: t('appearance.classic'),
      designSystem: false,
      active: !designSystem.value,
    },
  ])

  /**
   * The controller answers with `Inertia::location`, so the client makes a full page visit of the
   * screen you switched from. That shell reload is what re-renders Blade's `data-ds-theme`; an
   * ordinary Inertia visit does not, and left one look's CSS tokens under the other's components.
   */
  const setAppearance = (useDesignSystem) => {
    if (designSystem.value === useDesignSystem) {
      return
    }

    router.put(
      useRoute('account.appearance.update'),
      { design_system: useDesignSystem },
      {},
    )
  }

  return { available, options, setAppearance }
}
